import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useGetComponent } from 'hooks/useGetComponent';
import { componentIds, pageIds } from 'utilities/constants';
import {
  Hasura_Test_Types_Enum,
  useAddSwabCodesMutation,
} from 'graphql/generated/hasura';
import { useCustomToast } from 'layout/useToast';
import { SwabCodes } from '../../pages/test-kits/list/AvailableTestKitIDsTable/useUploadKitsByFile';
import { useEffect } from 'react';
import { useGetPage } from 'hooks/useGetPage';
import { DnaTypes } from '../../../manage-test-kits/components/TestKitTable/ActionButtons';

export const format8Char = /^[a-zA-Z0-9]{8}$/; // XXXXXXXC -> 8 alphanumeric characters
export const format6Digit = /^\d{6}$/; // XXXXXX -> 6 numeric digits only
export const format7ZRT = /^[a-z0-9]{7}$/; // XXXXXXX -> 7 lowercase letters or digits
export const formatDNA = /^[A-Z0-9]{7}-[A-Z0-9]{2}$/;

export const useCreateManualTests = (
  codes: SwabCodes[] = [
    {
      swabCode: '',
      testType: '' as Hasura_Test_Types_Enum,
    },
  ],
) => {
  const { data: locale } = useGetComponent({
    locale: 'en',
    componentId: componentIds.CREATE_TEST_KIDS_MODAL,
  });

  const { data: localeTypes } = useGetPage({
    locale: 'en',
    pageId: pageIds.SUPER_ADMIN_TEST_KITS,
  });

  const { addToast } = useCustomToast();

  const schema = yup.object({
    tests: yup.array(
      yup.object({
        swabCode: yup
          .string()
          .required(locale?.required ?? 'Field is required')
          .test(
            'test-kit-id-format',
            locale?.invalidFormat ?? 'Invalid format',
            function (value) {
              if (!value) {
                return true;
              }
              const testType = this.parent.testType;
              console.log('value', value);
              if (DnaTypes.includes(testType)) {
                return formatDNA.test(value);
              }
              return (
                format8Char.test(value) ||
                format6Digit.test(value) ||
                format7ZRT.test(value) ||
                formatDNA.test(value)
              );
            },
          ),
        testType: yup
          .string()
          .required(locale?.required ?? 'Field is required'),
      }),
    ),
  });

  const [addSwabCodes, { loading }] = useAddSwabCodesMutation({
    refetchQueries: ['GetAvailableSwabCodes'],
    onCompleted: (data) => {
      if (!data) {
        return;
      }
      const {
        existingSwabCodes,
        invalidSwabCodesDueSomeIssue,
        invalidSwabCodesDueToTypes,
        totalAdded,
      } = data.AddSwabCodes ?? {
        existingSwabCodes: [],
        invalidSwabCodesDueSomeIssue: [],
        invalidSwabCodesDueToTypes: [],
      };
      if (
        invalidSwabCodesDueSomeIssue.length ||
        invalidSwabCodesDueToTypes.length ||
        existingSwabCodes.length
      ) {
        let message = `${totalAdded} ${localeTypes?.testKitUpload} \n`;
        if (invalidSwabCodesDueSomeIssue.length) {
          message += `\n ${
            localeTypes?.invalidSwabCodesDueSomeIssue
          } ${invalidSwabCodesDueSomeIssue.join(', ')}`;
        }
        if (invalidSwabCodesDueToTypes.length) {
          message += `\n ${
            localeTypes?.invalidSwabCodesDueToTypes
          } ${invalidSwabCodesDueToTypes.join(', ')}`;
        }
        if (existingSwabCodes.length) {
          message += `\n ${
            localeTypes?.existingSwabCodes
          } ${existingSwabCodes.join(', ')}`;
        }
        return addToast({
          type: 'info-new',
          message: message.trim(),
        });
      }
      return addToast({
        type: 'success',
        message: `${totalAdded} ${localeTypes?.testKitUpload}`,
      });
    },
  });

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      tests: [
        {
          swabCode: '',
          testType: '' as Hasura_Test_Types_Enum,
        },
      ],
    },
  });

  useEffect(() => {
    if (codes.length) {
      methods.setValue('tests', codes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codes]);

  const handleSubmit = (data: yup.InferType<typeof schema>) => {
    const formData = data?.tests?.map(({ swabCode, testType }) => ({
      testType: testType as Hasura_Test_Types_Enum,
      swabCode,
    }));
    return addSwabCodes({
      variables: { swabCodes: formData },
    });
  };

  return {
    methods,
    handleSubmit,
    loading,
  };
};
